// OpenAI service for analyzing job descriptions and matching with portfolio

interface AnalysisResult {
  relevantCaseStudies: {
    slug: string
    title: string
    relevance: number
    reason: string
  }[]
  relevantSkills: {
    skill: string
    category: string
    relevance: number
    description: string
  }[]
  summary: string
  jobTitle: string
  companyName?: string
  keyRequirements: string[]
  extractedKeywords: string[]
  matchedRequirements: {
    requirement: string
    matchedWith: string
  }[]
  sourceType?: 'url' | 'file' | 'text' | 'image'
  sourceDetails?: string
}
interface SearchResult {
  type: 'case-study' | 'skill' | 'general'
  caseStudy?: {
    slug: string
    title: string
    description: string
  }
  skills?: string[]
  answer?: string
}

// Default fallback data in case the API call fails
const fallbackAnalysisResult: AnalysisResult = {
  jobTitle: 'AI Product Designer',
  companyName: undefined,
  extractedKeywords: [
    'AI',
    'conversational design',
    'user experience',
    'platform evaluation',
  ],
  summary:
    "With your extensive experience in AI strategy, conversational design, and technical communication, you're an excellent match for this AI Product Designer role. Your work on the Watson vs Dialogflow project demonstrates your ability to evaluate AI platforms and create effective conversational interfaces.",
  keyRequirements: [
    'Experience with conversational design',
    'Knowledge of AI integration strategies',
    'Technical communication skills',
    'User experience design expertise',
    'Ability to translate complex concepts into intuitive designs',
  ],
  matchedRequirements: [
    {
      requirement: 'Experience with conversational design',
      matchedWith:
        'Watson vs Dialogflow project - created natural dialogue patterns',
    },
    {
      requirement: 'Knowledge of AI integration strategies',
      matchedWith:
        'AI Strategy skill - comprehensive AI integration experience',
    },
    {
      requirement: 'User experience design expertise',
      matchedWith: 'Multiple case studies demonstrating UX expertise',
    },
  ],
  relevantSkills: [
    {
      skill: 'AI Strategy',
      category: 'Technical Skills',
      relevance: 0.95,
      description:
        'Developed comprehensive AI integration strategies for multiple projects, with expertise in aligning AI capabilities with business objectives.',
    },
    {
      skill: 'Conversational Design',
      category: 'Design Skills',
      relevance: 0.92,
      description:
        'Created natural dialogue patterns and conversation flows for AI assistants, ensuring efficient and human-like interactions.',
    },
    {
      skill: 'Technical Communication',
      category: 'Soft Skills',
      relevance: 0.88,
      description:
        'Expertise in translating complex AI concepts into clear, understandable language for stakeholders and end users.',
    },
    {
      skill: 'UX Design',
      category: 'Design Skills',
      relevance: 0.85,
      description:
        'Applied user-centered design principles to create intuitive interfaces for AI-powered products and services.',
    },
  ],
  relevantCaseStudies: [
    {
      slug: 'ai-comparison',
      title: 'Watson vs Dialogflow / Project EVE',
      relevance: 0.95,
      reason:
        'Directly demonstrates your experience with conversational AI platforms, including developing natural dialogue patterns and evaluating AI technologies.',
    },
    {
      slug: 'cyber-security',
      title: 'Cyber Security Platform Redesign',
      relevance: 0.8,
      reason:
        'Shows your ability to simplify complex technical systems into intuitive interfaces, a key skill for AI product design.',
    },
    {
      slug: 'us-bank',
      title: 'US Bank Commercial Card',
      relevance: 0.7,
      reason:
        'Demonstrates your experience designing for enterprise users and integrating with complex systems.',
    },
  ],
  sourceType: 'text',
}
import { searchCaseStudies } from './caseStudyKeywords'

/**
 * Detect if input is a URL
 */
function isUrl(input: string): boolean {
  try {
    const url = new URL(input.trim())
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * Extract company name from job posting URL patterns
 */
function extractCompanyFromUrl(url: string): string | null {
  try {
    const urlObj = new URL(url)
    const hostname = urlObj.hostname.toLowerCase()
    const pathname = urlObj.pathname.toLowerCase()

    // Greenhouse pattern: boards.greenhouse.io/[company-name]/jobs/...
    if (hostname.includes('greenhouse.io')) {
      const match = pathname.match(/\/([^\/]+)\/jobs/)
      if (match) {
        return formatCompanyName(match[1])
      }
    }

    // Lever pattern: jobs.lever.co/[company-name]/...
    if (hostname.includes('lever.co')) {
      const match = pathname.match(/\/([^\/]+)\//)
      if (match) {
        return formatCompanyName(match[1])
      }
    }

    // Workday pattern: [company].wd1.myworkdayjobs.com or [company].wd5.myworkdayjobs.com
    if (hostname.includes('myworkdayjobs.com')) {
      const match = hostname.match(/^([^\.]+)\.wd\d+\.myworkdayjobs\.com/)
      if (match) {
        return formatCompanyName(match[1])
      }
    }

    // BambooHR pattern: [company].bamboohr.com
    if (hostname.includes('bamboohr.com')) {
      const match = hostname.match(/^([^\.]+)\.bamboohr\.com/)
      if (match) {
        return formatCompanyName(match[1])
      }
    }

    // Ashby pattern: jobs.ashbyhq.com/[company-name]
    if (hostname.includes('ashbyhq.com')) {
      const match = pathname.match(/\/([^\/]+)/)
      if (match) {
        return formatCompanyName(match[1])
      }
    }

    // Company career pages: [company].com/careers or careers.[company].com
    if (pathname.includes('/careers') || pathname.includes('/jobs')) {
      // Remove common TLDs and subdomains
      const domainMatch = hostname.match(/([^\.]+)\.(com|io|co|net|org)$/)
      if (
        domainMatch &&
        !['jobs', 'careers', 'apply'].includes(domainMatch[1])
      ) {
        return formatCompanyName(domainMatch[1])
      }
    }
    return null
  } catch {
    return null
  }
}

/**
 * Format company name from URL slug to proper case
 */
function formatCompanyName(slug: string): string {
  return slug
    .split(/[-_]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Scrape job posting content from URL using a simple fetch
 * Falls back to GPT inference if scraping fails
 */
async function scrapeJobPostingUrl(url: string): Promise<string> {
  console.log('🌐 Attempting to scrape URL:', url)
  try {
    // Try to fetch the URL directly (may be blocked by CORS)
    const response = await fetch(url, {
      headers: {
        'User-Agent': 'Mozilla/5.0 (compatible; JobAnalyzer/1.0)',
      },
    })
    if (response.ok) {
      const html = await response.text()
      console.log('✅ Successfully fetched URL content')

      // Extract text content from HTML (basic extraction)
      const textContent = html
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
      console.log('📝 Extracted text length:', textContent.length)
      return textContent.substring(0, 8000) // Limit to 8000 chars
    }
  } catch (error) {
    console.log('⚠️ Direct fetch failed (likely CORS), will use GPT inference')
  }

  // Fallback: Return URL for GPT to analyze
  return `Job Posting URL: ${url}\n\nNote: Please analyze this job posting URL and extract as much information as possible from the URL structure itself (company name, job title, platform, etc.). Then provide your best matching analysis based on typical requirements for this type of role.`
}

/**
 * Hybrid search: Try keyword search first, fall back to GPT for complex queries
 */
export async function searchPortfolio(query: string): Promise<SearchResult> {
  console.log('🔍 searchPortfolio called with query:', query)

  // STEP 1: Try keyword-based search first (fast, hardcoded)
  console.log('📋 Attempting keyword-based search first...')
  const keywordResults = searchCaseStudies(query)

  // If we have strong keyword matches (score > 15), use those
  if (keywordResults.length > 0 && keywordResults[0].score > 15) {
    console.log('✅ Strong keyword match found:', keywordResults[0])
    const topMatch = keywordResults[0]

    // Map slug to case study details
    const caseStudyMap = {
      'us-bank': {
        title: 'US Bank Commercial Card',
        description:
          'Financial services project improving customer satisfaction',
      },
      'cyber-security': {
        title: 'Cyber Security Platform Redesign',
        description:
          'Complex data visualization and security platform redesign',
      },
      'ai-comparison': {
        title: 'Watson vs Dialogflow / Project EVE',
        description: 'Conversational AI platform comparison',
      },
      'case-western': {
        title: 'Case Western Reserve University',
        description: 'UX/UI redesign with extensive user research',
      },
    }
    const caseStudy = caseStudyMap[topMatch.slug]
    if (caseStudy) {
      console.log('✅ Returning keyword-based result (no GPT needed)')
      return {
        type: 'case-study',
        caseStudy: {
          slug: topMatch.slug,
          title: caseStudy.title,
          description: caseStudy.description,
        },
      }
    }
  }

  // STEP 2: If keyword search failed or returned weak results, fall back to GPT
  console.log('🤖 Keyword search insufficient, falling back to GPT...')
  console.log('🔍 AI will now scan entire portfolio for relevant experience...')
  try {
    console.log('📤 Sending request to OpenAI proxy...')
    const response = await fetch(
      'https://requests.magicpatterns.com/openai/proxy',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4-turbo',
          messages: [
            {
              role: 'system',
              content: `You are a portfolio search assistant for Mateo Cohen, a Full Stack Designer and AI Specialist.

Your task is to scan Mateo's ENTIRE portfolio and resume to find the most relevant case study that matches the user's search query.

### MATEO'S COMPLETE PORTFOLIO - SCAN ALL OF THIS:

1. **US Bank Commercial Card** (slug: us-bank)
   - Role: UX/UI Designer on Corporate Payment Services team
   - Project: One Card application redesign
   - Impact: Improved customer satisfaction and team productivity
   - Skills Demonstrated: UX Design, UI Design, Financial Services, Customer Experience, Enterprise Design, B2B Applications
   - Domain: Financial services, banking, corporate payments, enterprise applications
   - Keywords: financial, banking, payment, corporate card, enterprise, B2B, financial services, customer satisfaction, team productivity

2. **Cyber Security Platform - Unisys Stealth** (slug: cyber-security)
   - Role: Designer on large platform redesign
   - Project: Cybersecurity micro segmentation and threat analysis applications
   - Impact: Transformed complex technical systems into intuitive, user-centered solutions
   - Skills Demonstrated: UX Design, UI Design, Technical Communication, Complex System Simplification, Data Visualization, Enterprise Security
   - Domain: Cybersecurity, network security, enterprise security, data visualization
   - Keywords: security, cybersecurity, data, complex data, data-heavy, technical, enterprise security, threat analysis, network security, micro segmentation, visualization

3. **Watson vs Dialogflow / Project EVE - MoneyGram** (slug: ai-comparison)
   - Role: Conversational AI Designer and Platform Evaluator
   - Project: Comprehensive analysis of conversational AI platforms (IBM Watson vs Google Dialogflow)
   - Impact: Created working demos, developed natural dialogue patterns for customer service automation
   - Skills Demonstrated: AI Strategy, Conversational Design, Technical Communication, Platform Evaluation, NLP, Chatbot Design
   - Domain: Artificial intelligence, conversational AI, chatbots, automation
   - Keywords: AI, artificial intelligence, conversational design, chatbot, NLP, natural language, voice interface, automation, Watson, Dialogflow, MoneyGram

4. **Case Western Reserve University - Faculty Information System** (slug: case-western)
   - Role: Strategic Product Designer leading UX/UI redesign
   - Project: Faculty Information System comprehensive redesign with AI innovation
   - Impact: Led qualitative research (16 user interviews), created AI-powered CV parsing solution (85%+ accuracy), secured Amazon pilot program
   - Skills Demonstrated: UX Research, Product Design, AI Innovation, User Testing, Iterative Design, Qualitative Research, Design Leadership
   - Domain: Education, university systems, academic platforms, AI-powered solutions
   - Keywords: university, education, research, user research, qualitative research, AI innovation, faculty, academic, UX research, design leadership, CV parsing

### CORE SKILLS ACROSS ALL PROJECTS:
- **AI Strategy & Innovation**: AI integration, conversational design, AI-powered solutions, automation
- **UX/UI Design**: User-centered design, visual design, interaction design, design systems
- **Research & Testing**: Qualitative research, user interviews, usability testing, iterative design
- **Technical Communication**: Simplifying complex systems, stakeholder collaboration, technical documentation
- **Domain Expertise**: Financial services, cybersecurity, AI/ML, education technology
- **Design Leadership**: Leading design initiatives, cross-functional collaboration, strategic thinking

### SEARCH MATCHING RULES:
1. **Financial/Banking/Payment queries** → US Bank (slug: us-bank)
2. **Data/Security/Technical/Complex Systems queries** → Cyber Security (slug: cyber-security)
3. **AI/Chatbot/Automation/NLP queries** → Watson vs Dialogflow (slug: ai-comparison)
4. **Research/University/Academic/User Research queries** → Case Western (slug: case-western)

CRITICAL: You MUST scan ALL case studies and return the MOST relevant match. Always return type "case-study" with a slug.

Return JSON in this EXACT format (no markdown, no code blocks):
{
  "type": "case-study",
  "caseStudy": {
    "slug": "us-bank" or "cyber-security" or "ai-comparison" or "case-western",
    "title": "Case Study Title",
    "description": "Brief description"
  }
}`,
            },
            {
              role: 'user',
              content: `Search Query: "${query}"

Scan Mateo's entire portfolio and resume to find the most relevant case study that matches this query. Consider all skills, projects, and experience across all four case studies.`,
            },
          ],
          temperature: 0.3,
          max_tokens: 300,
        }),
      },
    )
    console.log('📥 Response status:', response.status)
    if (!response.ok) {
      console.error('❌ API request failed with status:', response.status)
      throw new Error(
        `Search API request failed with status ${response.status}`,
      )
    }
    const data = await response.json()
    console.log('📊 Raw API response:', JSON.stringify(data, null, 2))
    if (data.choices && data.choices[0]?.message?.content) {
      const content = data.choices[0].message.content
      console.log('📝 GPT response content:', content)
      let jsonString = content
      const jsonMatch =
        content.match(/```json\s*([\s\S]*?)\s*```/) ||
        content.match(/```\s*([\s\S]*?)\s*```/) ||
        content.match(/{[\s\S]*}/)
      if (jsonMatch) {
        jsonString = jsonMatch[1] || jsonMatch[0]
      }
      jsonString = jsonString.replace(/```json|```/g, '').trim()
      console.log('🔧 Cleaned JSON string:', jsonString)
      try {
        const result = JSON.parse(jsonString)
        console.log('✅ Parsed result:', result)
        if (result.type === 'case-study' && result.caseStudy?.slug) {
          console.log(
            '✅ Valid case study result, slug:',
            result.caseStudy.slug,
          )
          return result
        } else {
          console.warn('⚠️ Result missing expected structure, using fallback')
          return getFallbackCaseStudy(query)
        }
      } catch (parseError) {
        console.error('❌ JSON parse error:', parseError)
        return getFallbackCaseStudy(query)
      }
    }
    console.error('❌ Invalid response format from API')
    throw new Error('Invalid response format')
  } catch (error) {
    console.error('❌ Error searching portfolio:', error)
    return getFallbackCaseStudy(query)
  }
}

/**
 * Get a fallback case study based on query keywords
 */
function getFallbackCaseStudy(query: string): SearchResult {
  const queryLower = query.toLowerCase()
  console.log('🔄 Using fallback case study matcher for query:', queryLower)

  // Financial keywords
  if (
    queryLower.includes('financial') ||
    queryLower.includes('banking') ||
    queryLower.includes('payment') ||
    queryLower.includes('bank')
  ) {
    console.log('✅ Matched: US Bank (financial keywords)')
    return {
      type: 'case-study',
      caseStudy: {
        slug: 'us-bank',
        title: 'US Bank Commercial Card',
        description:
          'Financial services project improving customer satisfaction',
      },
    }
  }

  // Data/Security keywords
  if (
    queryLower.includes('data') ||
    queryLower.includes('security') ||
    queryLower.includes('cyber') ||
    queryLower.includes('complex')
  ) {
    console.log('✅ Matched: Cyber Security (data/security keywords)')
    return {
      type: 'case-study',
      caseStudy: {
        slug: 'cyber-security',
        title: 'Cyber Security Platform Redesign',
        description:
          'Complex data visualization and security platform redesign',
      },
    }
  }

  // AI keywords
  if (
    queryLower.includes('ai') ||
    queryLower.includes('chatbot') ||
    queryLower.includes('conversational') ||
    queryLower.includes('automation')
  ) {
    console.log('✅ Matched: AI Comparison (AI keywords)')
    return {
      type: 'case-study',
      caseStudy: {
        slug: 'ai-comparison',
        title: 'Watson vs Dialogflow / Project EVE',
        description: 'Conversational AI platform comparison',
      },
    }
  }

  // Research keywords
  if (
    queryLower.includes('research') ||
    queryLower.includes('university') ||
    queryLower.includes('academic') ||
    queryLower.includes('user research')
  ) {
    console.log('✅ Matched: Case Western (research keywords)')
    return {
      type: 'case-study',
      caseStudy: {
        slug: 'case-western',
        title: 'Case Western Reserve University',
        description: 'UX/UI redesign with extensive user research',
      },
    }
  }

  // Default to US Bank for general queries
  console.log('ℹ️ No specific match, defaulting to US Bank')
  return {
    type: 'case-study',
    caseStudy: {
      slug: 'us-bank',
      title: 'US Bank Commercial Card',
      description: 'Financial services project improving customer satisfaction',
    },
  }
}

/**
 * Convert file to base64 data URL
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result)
      } else {
        reject(new Error('Failed to convert file to base64'))
      }
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

/**
 * Analyze an image of a job description using GPT Vision
 */
export async function analyzeJobDescriptionImage(
  imageFile: File,
): Promise<AnalysisResult> {
  try {
    console.log('📸 Analyzing job description image:', imageFile.name)

    // Convert image to base64
    const base64Image = await fileToBase64(imageFile)
    const response = await fetch(
      'https://requests.magicpatterns.com/openai/proxy',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4-vision-preview',
          messages: [
            {
              role: 'system',
              content: `You are analyzing a job description image to extract key information and match it with Mateo Cohen's portfolio.

CRITICAL: Extract EXACT details from the image to prove you read it:
- Job title (exact as shown)
- Company name (if visible)
- Specific requirements, skills, technologies mentioned
- Any unique details or keywords

Return the extracted text in plain format so it can be analyzed further.`,
            },
            {
              role: 'user',
              content: [
                {
                  type: 'text',
                  text: 'Extract ALL text from this job description image. Include the job title, company name, requirements, qualifications, and any other details visible. Return as plain text.',
                },
                {
                  type: 'image_url',
                  image_url: {
                    url: base64Image,
                  },
                },
              ],
            },
          ],
          max_tokens: 2500,
        }),
      },
    )
    if (!response.ok) {
      console.error(`Vision API request failed with status ${response.status}`)
      throw new Error('Failed to analyze image')
    }
    const data = await response.json()
    const extractedText = data.choices[0].message.content
    console.log('✅ Extracted text from image:', extractedText)

    // Now analyze the extracted text using the regular job description analyzer
    const result = await analyzeJobDescription(extractedText)
    result.sourceType = 'image'
    result.sourceDetails = imageFile.name
    return result
  } catch (error) {
    console.error('Error analyzing job description image:', error)
    return {
      ...fallbackAnalysisResult,
      sourceType: 'image',
      sourceDetails: 'uploaded image',
    }
  }
}

/**
 * Analyze a job description from a URL
 * Attempts to scrape content, falls back to GPT inference
 */
export async function analyzeJobDescriptionFromUrl(
  url: string,
): Promise<AnalysisResult> {
  try {
    console.log('🔗 Analyzing job description from URL:', url)

    // Try to scrape the URL content
    const scrapedContent = await scrapeJobPostingUrl(url)
    console.log('📝 Scraped content length:', scrapedContent.length)
    console.log('📝 First 300 chars:', scrapedContent.substring(0, 300))

    // Check if we got actual content or just the URL fallback message
    const isActualContent =
      scrapedContent.length > 500 &&
      !scrapedContent.startsWith('Job Posting URL:')
    if (isActualContent) {
      console.log('✅ Got actual scraped content, analyzing...')
      // Analyze the scraped content
      const result = await analyzeJobDescription(scrapedContent)
      result.sourceType = 'url'
      result.sourceDetails = url
      return result
    } else {
      console.log(
        '⚠️ Could not scrape content, using enhanced GPT URL analysis...',
      )
      // Enhanced GPT analysis specifically for URLs
      return await analyzeJobDescriptionFromUrlOnly(url)
    }
  } catch (error) {
    console.error('Error analyzing job description from URL:', error)
    return {
      ...fallbackAnalysisResult,
      sourceType: 'url',
      sourceDetails: url,
    }
  }
}

/**
 * Analyze a job posting URL when scraping fails
 * Uses GPT to make intelligent inferences from the URL structure
 */
async function analyzeJobDescriptionFromUrlOnly(
  url: string,
): Promise<AnalysisResult> {
  try {
    console.log('🤖 Using GPT to analyze URL structure:', url)

    // First, try to extract company name from URL pattern
    const extractedCompany = extractCompanyFromUrl(url)
    console.log(
      '🏢 Extracted company from URL:',
      extractedCompany || 'None found',
    )
    const response = await fetch(
      'https://requests.magicpatterns.com/openai/proxy',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4-turbo',
          messages: [
            {
              role: 'system',
              content: `You are analyzing a job posting URL for Mateo Cohen's portfolio matching.

CRITICAL PRIORITY: Extract the COMPANY NAME from the URL. This is the #1 most important piece of information.

Common URL patterns to extract company name from:
- Greenhouse: boards.greenhouse.io/[COMPANY]/jobs/...
- Lever: jobs.lever.co/[COMPANY]/...
- Workday: [COMPANY].wd1.myworkdayjobs.com/...
- BambooHR: [COMPANY].bamboohr.com/...
- Ashby: jobs.ashbyhq.com/[COMPANY]/...
- Career pages: [COMPANY].com/careers or careers.[COMPANY].com

${extractedCompany ? `\n**COMPANY DETECTED FROM URL: "${extractedCompany}"**\nYou MUST include this company name in your response.\n` : ''}

### MATEO'S PORTFOLIO:

**Case Studies:**
1. **US Bank** (slug: us-bank)
   - Financial services, corporate payments, B2B applications
   - UX/UI Design, Customer Experience, Enterprise Design
   
2. **Cyber Security** (slug: cyber-security)
   - Security platform redesign, data visualization
   - Complex system simplification, technical communication
   
3. **Watson vs Dialogflow** (slug: ai-comparison)
   - Conversational AI, chatbot design, NLP
   - AI strategy, platform evaluation
   
4. **Case Western** (slug: case-western)
   - UX research, product design, AI innovation
   - User testing, qualitative research

**Key Skills:**
- AI Strategy & Conversational Design
- UX/UI Design & Research
- Financial Services Experience
- Data Visualization
- Technical Communication

### YOUR TASK:
1. FIRST: Extract the company name from the URL (if not already provided above)
2. SECOND: Infer the job title from the URL structure
3. THIRD: Provide detailed matching analysis

Return ONLY valid JSON (no markdown, no code blocks):
{
  "jobTitle": "Specific inferred job title (e.g., 'Senior Product Designer' not 'Job Title')",
  "companyName": "${extractedCompany || 'EXTRACT FROM URL - DO NOT leave null if company is visible in URL'}",
  "extractedKeywords": ["specific", "relevant", "keywords", "for", "this", "role"],
  "summary": "Detailed 2-3 sentence summary of how Mateo's experience matches this specific role type at [COMPANY NAME]",
  "keyRequirements": [
    "Specific requirement 1 for this role type",
    "Specific requirement 2 for this role type",
    "Specific requirement 3 for this role type",
    "Specific requirement 4 for this role type"
  ],
  "matchedRequirements": [
    {
      "requirement": "Specific requirement from typical job",
      "matchedWith": "Specific Mateo skill or case study that matches"
    },
    {
      "requirement": "Another specific requirement",
      "matchedWith": "Another specific match"
    }
  ],
  "relevantSkills": [
    {
      "skill": "Specific skill name",
      "category": "Design Skills",
      "relevance": 0.9,
      "description": "How this skill applies to this role type"
    },
    {
      "skill": "Another skill",
      "category": "Technical Skills",
      "relevance": 0.85,
      "description": "How this applies"
    }
  ],
  "relevantCaseStudies": [
    {
      "slug": "most-relevant-slug",
      "title": "Case Study Title",
      "relevance": 0.9,
      "reason": "Why this case study is relevant to this role"
    },
    {
      "slug": "second-relevant-slug",
      "title": "Another Case Study",
      "relevance": 0.8,
      "reason": "Why this is also relevant"
    }
  ]
}

CRITICAL: If you can see a company name in the URL, you MUST include it in companyName field. Do not leave it null.`,
            },
            {
              role: 'user',
              content: `Analyze this job posting URL and provide detailed matching analysis:

${url}

${extractedCompany ? `\nCompany detected from URL: ${extractedCompany}\n` : ''}

IMPORTANT: 
1. Extract the company name from the URL structure
2. Infer the job title and role type
3. Provide specific matching analysis for Mateo's portfolio

Provide a thorough, specific analysis with the company name clearly identified.`,
            },
          ],
          temperature: 0.7,
          max_tokens: 2500,
        }),
      },
    )
    console.log('📥 URL Analysis Response Status:', response.status)
    if (!response.ok) {
      const errorText = await response.text()
      console.error(`❌ URL analysis API failed with status ${response.status}`)
      console.error('❌ Error details:', errorText)
      throw new Error(`API request failed: ${response.status}`)
    }
    const data = await response.json()
    console.log('📊 Raw response received from GPT')
    if (!data.choices || !data.choices[0]?.message?.content) {
      console.error('❌ Invalid response structure:', data)
      throw new Error('Invalid API response structure')
    }
    const content = data.choices[0].message.content
    console.log('📝 GPT Response Length:', content.length)
    console.log('📝 First 500 chars:', content.substring(0, 500))
    let jsonString = content

    // Try to extract JSON from markdown code blocks
    const jsonMatch =
      content.match(/```json\s*([\s\S]*?)\s*```/) ||
      content.match(/```\s*([\s\S]*?)\s*```/) ||
      content.match(/{[\s\S]*}/)
    if (jsonMatch) {
      jsonString = jsonMatch[1] || jsonMatch[0]
      console.log('✅ Extracted JSON from markdown')
    }
    jsonString = jsonString.replace(/```json|```/g, '').trim()
    try {
      const result = JSON.parse(jsonString) as AnalysisResult

      // If we extracted company from URL but GPT didn't include it, use our extraction
      if (extractedCompany && !result.companyName) {
        result.companyName = extractedCompany
        console.log('✅ Applied extracted company name:', extractedCompany)
      }

      // Validate the result has required fields
      if (!result.jobTitle || !result.summary || !result.keyRequirements) {
        console.error('❌ Parsed result missing required fields:', result)
        throw new Error('Incomplete analysis result')
      }
      result.sourceType = 'url'
      result.sourceDetails = url
      console.log('✅ Successfully analyzed URL')
      console.log('📊 Job Title:', result.jobTitle)
      console.log('📊 Company:', result.companyName || 'Not specified')
      console.log('📊 Keywords:', result.extractedKeywords?.length || 0)
      console.log('📊 Skills:', result.relevantSkills?.length || 0)
      console.log('📊 Case Studies:', result.relevantCaseStudies?.length || 0)
      return result
    } catch (parseError) {
      console.error('❌ Failed to parse JSON from GPT response')
      console.error('❌ Parse error:', parseError)
      console.error(
        '❌ JSON string that failed:',
        jsonString.substring(0, 1000),
      )
      throw parseError
    }
  } catch (error) {
    console.error('❌ Complete failure in URL-only analysis:', error)
    console.log('⚠️ Returning fallback result')

    // Try to use extracted company in fallback
    const fallbackWithCompany = {
      ...fallbackAnalysisResult,
    }
    const extractedCompany = extractCompanyFromUrl(url)
    if (extractedCompany) {
      fallbackWithCompany.companyName = extractedCompany
    }
    return {
      ...fallbackWithCompany,
      sourceType: 'url',
      sourceDetails: url,
    }
  }
}

export async function analyzeJobDescription(
  jobDescription: string,
): Promise<AnalysisResult> {
  console.log('🔍 analyzeJobDescription called')
  console.log('📝 Job description length:', jobDescription.length)
  console.log('📝 First 200 chars:', jobDescription.substring(0, 200))
  try {
    console.log('📤 Sending request to OpenAI proxy...')
    console.log('🔍 AI will scan entire portfolio to match job requirements...')

    // Using the Magic Patterns proxy service for OpenAI API calls
    const response = await fetch(
      'https://requests.magicpatterns.com/openai/proxy',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4-turbo',
          messages: [
            {
              role: 'system',
              content: `You are a professional job description analyzer for Mateo Cohen, a Full Stack Designer and AI Specialist. 
Your task is to analyze job descriptions and identify how Mateo's skills and case studies align with the requirements.

CRITICAL: You MUST scan Mateo's ENTIRE portfolio and resume to find ALL relevant experience. Extract specific details from the job description to prove you read it. This includes:
- Job title (extract the exact title from the description)
- Company name (if mentioned)
- Specific requirements, skills, or keywords mentioned
- Technical terms, tools, or technologies listed

### MATEO'S COMPLETE PORTFOLIO - SCAN ALL OF THIS FOR MATCHES:

1. **US Bank Commercial Card** (slug: us-bank)
   - Role: UX/UI Designer on Corporate Payment Services team
   - Project: One Card application redesign for corporate payments
   - Impact: Improved customer satisfaction and team productivity
   - Key Achievements:
     * Redesigned complex payment workflows for enterprise users
     * Enhanced user experience for corporate card management
     * Improved team collaboration and productivity
     * Worked with financial services compliance and security requirements
   - Skills Demonstrated: UX Design, UI Design, Financial Services, Customer Experience, Enterprise Design, B2B Applications, Payment Systems, Corporate Banking
   - Relevant For: Financial services roles, banking positions, enterprise software, B2B applications, payment processing, customer experience roles

2. **Cyber Security Platform - Unisys Stealth** (slug: cyber-security)
   - Role: Designer on large platform redesign
   - Project: Cybersecurity micro segmentation and threat analysis applications
   - Impact: Transformed complex technical systems into intuitive, user-centered solutions
   - Key Achievements:
     * Redesigned complex data visualization for security threats
     * Simplified micro segmentation workflows
     * Created intuitive interfaces for technical security professionals
     * Balanced technical depth with usability
   - Skills Demonstrated: UX Design, UI Design, Technical Communication, Complex System Simplification, Data Visualization, Enterprise Security, Network Security, Threat Analysis
   - Relevant For: Security roles, data visualization positions, technical product design, enterprise software, complex system design, B2B technical products

3. **Watson vs Dialogflow / Project EVE - MoneyGram** (slug: ai-comparison)
   - Role: Conversational AI Designer and Platform Evaluator
   - Project: Comprehensive analysis and implementation of conversational AI platforms (IBM Watson vs Google Dialogflow)
   - Impact: Created working demos, developed natural dialogue patterns for customer service automation
   - Key Achievements:
     * Evaluated and compared major AI platforms (Watson, Dialogflow, Microsoft, Amazon)
     * Built working conversational AI demos ("Track Status", "Fee Lookup")
     * Designed natural dialogue patterns and conversation flows
     * Reduced call center volume through intelligent automation
     * Created AI strategy for enterprise implementation
   - Skills Demonstrated: AI Strategy, Conversational Design, Technical Communication, Platform Evaluation, NLP, Chatbot Design, Voice Interface Design, Customer Service Automation, AI Integration
   - Relevant For: AI/ML roles, conversational design, chatbot development, NLP positions, automation roles, customer service technology, voice interface design

4. **Case Western Reserve University - Faculty Information System** (slug: case-western)
   - Role: Strategic Product Designer leading comprehensive UX/UI redesign
   - Project: Faculty Information System redesign with AI-powered innovation
   - Impact: Led qualitative research (16 user interviews in 2 weeks), created AI-powered CV parsing solution (85%+ accuracy), secured Amazon pilot program
   - Key Achievements:
     * Led rapid qualitative research sprint (16 interviews, 2 weeks)
     * Conducted affinity diagramming and user research analysis
     * Developed AI-powered proof of concept for CV parsing
     * Achieved 85%+ accuracy in automated data extraction
     * Secured 2-month Amazon pilot program
     * Reduced manual data entry from 30 minutes to seconds
     * Scheduled for 2026 full implementation
   - Skills Demonstrated: UX Research, Product Design, AI Innovation, User Testing, Iterative Design, Qualitative Research, Design Leadership, Rapid Prototyping, Stakeholder Management, AI Integration, Python Collaboration
   - Relevant For: Product design roles, UX research positions, AI innovation roles, design leadership, user research, rapid prototyping, education technology, enterprise software

### MATEO'S COMPREHENSIVE SKILL SET:

**AI & Innovation:**
- AI Strategy & Integration (proven with Watson/Dialogflow comparison and CWRU CV parsing)
- Conversational Design & NLP (MoneyGram chatbot project)
- AI-Powered Solutions (85%+ accuracy CV parsing, Amazon pilot program)
- Platform Evaluation (compared Watson, Dialogflow, Microsoft, Amazon)
- Automation Strategy (call center automation, data entry automation)

**UX/UI Design:**
- User-Centered Design (all projects demonstrate this core skill)
- Visual Design & Interface Design (cybersecurity platform, banking app)
- Interaction Design (conversational flows, complex workflows)
- Design Systems (enterprise-level consistency)
- Accessibility & Usability (focus across all projects)

**Research & Testing:**
- Qualitative Research (16 interviews in 2-week sprint at CWRU)
- User Interviews & Testing (extensive experience across projects)
- Affinity Diagramming & Analysis (CWRU research synthesis)
- Usability Testing (iterative testing across all projects)
- Rapid Prototyping (fast iteration with stakeholders)

**Technical Skills:**
- Technical Communication (simplifying complex systems for stakeholders)
- Data Visualization (cybersecurity threat analysis, complex data)
- Complex System Simplification (security platform, banking workflows)
- Cross-Functional Collaboration (worked with Python engineers, stakeholders)
- Currently Learning: Python, AI Development, Programming Fundamentals

**Domain Expertise:**
- Financial Services (US Bank corporate payments)
- Cybersecurity (Unisys Stealth platform)
- AI/ML & Conversational AI (Watson vs Dialogflow)
- Education Technology (CWRU Faculty Information System)
- Enterprise Software (B2B applications across all projects)

**Leadership & Strategy:**
- Design Leadership (led CWRU redesign, strategic decisions)
- Stakeholder Management (collaborated across departments)
- Strategic Thinking (AI strategy, platform evaluation)
- Project Management (2-month sprints, tight deadlines)
- Cross-Functional Collaboration (designers, engineers, stakeholders)

Based on the job description provided, analyze how Mateo's skills and case studies align with the requirements. 

IMPORTANT: You MUST scan ALL case studies and pull relevant experience from across Mateo's entire portfolio. Extract specific details from the job description to prove you analyzed it. Return a JSON response with the following structure:
{
  "jobTitle": "EXACT job title from the description",
  "companyName": "Company name if mentioned, otherwise null",
  "extractedKeywords": ["keyword1", "keyword2", ...],
  "summary": "Comprehensive summary that references SPECIFIC requirements from the job description and shows how Mateo's experience across ALL relevant case studies matches them. Mention specific projects and achievements.",
  "keyRequirements": ["Requirement 1 AS WRITTEN in job description", "Requirement 2 AS WRITTEN", ...],
  "matchedRequirements": [
    {
      "requirement": "Specific requirement from job description",
      "matchedWith": "Which of Mateo's case studies, skills, or specific achievements matches this (be specific about which project)"
    }
  ],
  "relevantSkills": [
    {
      "skill": "Skill name",
      "category": "Technical/Design/Soft Skills",
      "relevance": 0.95,
      "description": "How this skill SPECIFICALLY matches requirements mentioned in the job description, with reference to which case study demonstrates it"
    }
  ],
  "relevantCaseStudies": [
    {
      "slug": "case-study-slug",
      "title": "Case Study Title",
      "relevance": 0.95,
      "reason": "Why this case study demonstrates experience relevant to SPECIFIC requirements from the job description. Include specific achievements and outcomes from the project."
    }
  ]
}

CRITICAL RULES:
1. SCAN ALL FOUR CASE STUDIES for relevant experience
2. Extract the EXACT job title as written in the description
3. Extract the company name if mentioned
4. Pull out specific keywords, technologies, and requirements mentioned
5. In your summary and descriptions, reference SPECIFIC requirements from the job description
6. Show clear connections between job requirements and Mateo's experience across ALL relevant case studies
7. Include multiple case studies if they're relevant (don't limit to just one)
8. Reference specific achievements and outcomes from the case studies
9. The user should see that you scanned the entire portfolio, not just one project

Ensure your analysis is comprehensive, accurate, and directly tied to the SPECIFIC job requirements provided. Show that you scanned ALL of Mateo's work to find the best matches.`,
            },
            {
              role: 'user',
              content: jobDescription,
            },
          ],
          temperature: 0.7,
          max_tokens: 2500,
        }),
      },
    )
    console.log('📥 Response status:', response.status)
    console.log('📥 Response ok:', response.ok)
    if (!response.ok) {
      const errorText = await response.text()
      console.error(`❌ API request failed with status ${response.status}`)
      console.error('❌ Error response:', errorText)
      console.log('⚠️ Returning fallback result due to API error')
      return {
        ...fallbackAnalysisResult,
        sourceType: 'text',
      }
    }
    const data = await response.json()
    console.log('📊 Raw API response received')
    try {
      let result: AnalysisResult
      if (data.choices && data.choices[0]?.message?.content) {
        const content = data.choices[0].message.content
        console.log('📝 GPT response content length:', content.length)
        console.log(
          '📝 First 300 chars of response:',
          content.substring(0, 300),
        )
        let jsonString = content
        const jsonMatch =
          content.match(/```json\s*([\s\S]*?)\s*```/) ||
          content.match(/```\s*([\s\S]*?)\s*```/) ||
          content.match(/{[\s\S]*}/)
        if (jsonMatch) {
          jsonString = jsonMatch[1] || jsonMatch[0]
          console.log('✅ Extracted JSON from markdown')
        }
        jsonString = jsonString.replace(/```json|```/g, '').trim()
        try {
          result = JSON.parse(jsonString)
          console.log('✅ Successfully parsed JSON response')
          console.log('✅ Job Title:', result.jobTitle)
          console.log('✅ Company:', result.companyName)
          console.log('✅ Skills count:', result.relevantSkills?.length)
          console.log(
            '✅ Case studies count:',
            result.relevantCaseStudies?.length,
          )
          if (
            !result.jobTitle ||
            !result.summary ||
            !Array.isArray(result.keyRequirements) ||
            !Array.isArray(result.relevantSkills) ||
            !Array.isArray(result.relevantCaseStudies)
          ) {
            console.error(
              '❌ Invalid response structure - missing required fields',
            )
            throw new Error('Invalid response structure')
          }

          // Set default sourceType if not already set
          if (!result.sourceType) {
            result.sourceType = 'text'
          }
          return result
        } catch (parseError) {
          console.error('❌ Failed to parse JSON response:', parseError)
          console.error(
            '❌ JSON string that failed:',
            jsonString.substring(0, 500),
          )
          console.log('⚠️ Returning fallback result due to parse error')
          return {
            ...fallbackAnalysisResult,
            sourceType: 'text',
          }
        }
      } else {
        console.error('❌ Invalid response format from OpenAI - no content')
        console.log('⚠️ Returning fallback result')
        return {
          ...fallbackAnalysisResult,
          sourceType: 'text',
        }
      }
    } catch (error) {
      console.error('❌ Error processing OpenAI response:', error)
      console.log('⚠️ Returning fallback result')
      return {
        ...fallbackAnalysisResult,
        sourceType: 'text',
      }
    }
  } catch (error) {
    console.error('❌ Error analyzing job description:', error)
    console.log('⚠️ Returning fallback result due to exception')
    return {
      ...fallbackAnalysisResult,
      sourceType: 'text',
    }
  }
}
export async function analyzeLinkedInProfileWithAI(
  profileUrl: string,
): Promise<{
  isRecruiter: boolean
  confidence: number
  reasoning: string
  detectedRole: string
}> {
  try {
    const response = await fetch(
      'https://requests.magicpatterns.com/openai/proxy',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4-turbo',
          messages: [
            {
              role: 'system',
              content: `You are an expert at analyzing LinkedIn profiles to detect if someone is a recruiter or talent acquisition professional. 

Analyze the LinkedIn profile URL and determine if this person is a recruiter based on:
- Job titles containing: recruiter, talent acquisition, talent partner, staffing, headhunter, HR specialist
- Company types: recruiting firms, staffing agencies, HR consulting
- Profile descriptions mentioning: hiring, recruiting, talent sourcing, candidate placement

Return a JSON response with:
{
  "isRecruiter": boolean,
  "confidence": number (0-100),
  "reasoning": "brief explanation",
  "detectedRole": "recruiter" | "hiring_manager" | "general" | "unknown"
}`,
            },
            {
              role: 'user',
              content: `Analyze this LinkedIn profile URL and determine if this person is a recruiter: ${profileUrl}

Based on the URL structure and any visible information, make your best assessment. If the URL contains indicators like "recruiter", "talent-acquisition", or similar terms in the username, factor that into your analysis.`,
            },
          ],
          temperature: 0.3,
          max_tokens: 300,
        }),
      },
    )
    if (!response.ok) {
      throw new Error('OpenAI API request failed')
    }
    const data = await response.json()
    const content = data.choices[0].message.content

    // Parse JSON response
    const analysis = JSON.parse(content)
    return {
      isRecruiter: analysis.isRecruiter || false,
      confidence: analysis.confidence || 0,
      reasoning: analysis.reasoning || '',
      detectedRole: analysis.detectedRole || 'unknown',
    }
  } catch (error) {
    console.error('OpenAI profile analysis error:', error)
    // Fallback to basic URL analysis
    return {
      isRecruiter: false,
      confidence: 0,
      reasoning: 'Analysis failed, using manual selection',
      detectedRole: 'unknown',
    }
  }
}
